import { useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { isAuthenticated } from '@alchemiesolns/product-registration';
import { INTENT_QUERY_KEY, appendIntentToPath } from '../auth/navigation';
import { useAuthUser } from '@alchemiesolns/user-access';
import { useLicense } from '@alchemiesolns/store-api';
import { tryEstablishLicense } from '../loginWithFirebase';
import { PRODUCT_ID } from '../constants/product';

/**
 * Post-Stripe return: poll `/licensesMe` until the webhook lands a license,
 * then establish the session and route into `/app`. Falls back to
 * `/upgrade?checkout=pending` if the poll times out.
 */
export function BillingReturnPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, ready } = useAuthUser();
  const license = useLicense(PRODUCT_ID, {
    enabled: ready && !!user,
    pollUntilActive: true,
  });

  useEffect(() => {
    if (!ready || !user) return;

    if (isAuthenticated()) {
      navigate(appendIntentToPath('/app', searchParams), { replace: true });
      return;
    }

    if (license.status !== 'active' && license.status !== 'past_due') return;

    let cancelled = false;
    void (async () => {
      const synced = await tryEstablishLicense(PRODUCT_ID);
      if (cancelled) return;
      if (synced) {
        navigate(appendIntentToPath('/app', searchParams), { replace: true });
      } else {
        const next = new URLSearchParams(searchParams);
        next.set('checkout', 'pending');
        navigate({ pathname: '/upgrade', search: next.toString() }, { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, user, license.status, navigate, searchParams]);

  useEffect(() => {
    if (!ready || !user) return;
    if (license.status === 'missing' || license.status === 'error') {
      // Either the polling deadline elapsed with no license, or the license
      // call errored. Send the user to the paywall with a pending hint.
      const next = new URLSearchParams(searchParams);
      next.set('checkout', 'pending');
      navigate({ pathname: '/upgrade', search: next.toString() }, { replace: true });
    }
  }, [ready, user, license.status, navigate, searchParams]);

  if (!ready) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center gap-2">
        <p className="text-slate-700 text-sm">Loading…</p>
      </div>
    );
  }

  if (!user) {
    const qs = new URLSearchParams();
    const intent = searchParams.get(INTENT_QUERY_KEY);
    if (intent) qs.set(INTENT_QUERY_KEY, intent);
    qs.set('return', '/billing/return');
    return <Navigate to={`/login?${qs.toString()}`} replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center gap-2">
      <p className="text-slate-700 text-sm">Finishing purchase…</p>
    </div>
  );
}
